import React, { HTMLAttributes, ReactNode } from "react";

import { FieldError } from "react-hook-form";
import classnames from "classnames";

import { useWombatTheme } from "../../../theme";

export type RadioGroupDisplay = "col" | "row";

type RadioGroupProps = {
  /**
   * Title of the radio group
   */
  title?: ReactNode;
  /**
   * Description of the radio group
   */
  description?: ReactNode;
  /**
   * Display type
   * Note: The respect the style guidelines,
   * if display is set to 'row' radios must be passed the 'boxed' props to true
   * @default 'col'
   */
  display?: RadioGroupDisplay;
  /**
   * Helper text, prefer to used this help text instead of individual ones
   * @default undefined
   */
  help?: ReactNode;
  /**
   * The react-hook-form error field
   * @default {}
   */
  error?: FieldError;
  /**
   * The input type
   * @default 'text'
   */
  /**
   * Disabled all descendant of the radio group
   * @default false
   */
  disabled?: boolean;
} & Omit<HTMLAttributes<HTMLFieldSetElement>, 'title'>;

/**
 * RadioGroup component is meant to receive Radio only children
 *
 * Note: Only one radio button in a given group can be selected at the same time.
 * To respect this behaviour, set all radio name to the same value
 */
const RadioGroup: React.FC<RadioGroupProps> = ({
  title,
  description,
  display = "col",
  disabled = false,
  help,
  error = {},
  children,
  ...props
}) => {
  const { theme } = useWombatTheme();

  const titleStyle = classnames(theme.form.radio.group.title, 'w-full');
  const descriptionStyle = classnames(theme.form.radio.group.description);
  const containerStyle = classnames(theme.form.radio.group.layout[display]);
  const helpStyle = classnames(
    theme.form.radio.group.help[error?.message ? "error" : "default"]
  );

  return (
    <fieldset {...props} disabled={disabled}>
      {title && (
        <legend className={titleStyle}>
          {title}
          {description && <div className={descriptionStyle}>{description}</div>}
        </legend>
      )}
      <div className={containerStyle}>{children}</div>
      {(help || error?.message) && (
        <div className={helpStyle}>{error?.message ? error?.message : help}</div>
      )}
    </fieldset>
  );
};

export default React.memo(RadioGroup);
